import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FormController } from "../../components/FormController/FormController";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { ProjectTypeSelect } from "../../components/SustainabilitySystem/SustainabilitySystemFields/ProjectTypeSelect/ProjectTypeSelect";
import { TYPE_NEW_BUILDING, TYPE_PAST } from "../../constants/sustainabilitySystem";
import { TENANT_TYPE_ESG, TENANT_TYPE_PAST } from "../../constants/tenant";
import { getUserTenantType } from "../../hooks/auth";
import { createSustainabilitySystem, useSustainabilitySystemCreateResponse } from "../../hooks/system";
import { showError, showSuccess } from "../../hooks/toast";
import { CloseSVG, SaveSVG } from "../../svg";
import { processMessage } from "../../utils";
import "./CreateSustainabilitySystem.scss";

export const CreateSustainabilitySystem = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantType = getUserTenantType();

  const response = useSustainabilitySystemCreateResponse();

  const { control, handleSubmit, getValues } = useForm({
    defaultValues: {
      name: "",
      systemSource: "",
      systemVersion: "",
      projectTypes: [tenantType === TENANT_TYPE_ESG ? TYPE_NEW_BUILDING : TYPE_PAST],
      description: "",
    },
  });

  const save = async () => {
    const values = getValues();
    try {
      const system = await createSustainabilitySystem({
        ...values,
        overallMaxSystemScore: 0,
        groupingElements: [],
      });
      showSuccess(t("sustainabilitySystem.createdMessage"));
      navigate("/sustainabilitySystem/" + system.id);
    } catch (error) {
      if (error.status === 409) {
        showError(processMessage(t("sustainabilitySystem.error.409"), [values.name, values.systemVersion]));
      } else {
        showError(t("sustainabilitySystem.error.500"));
      }
    }
  };

  const onSubmit = () => save();

  return (
    <LoadingOverlay spinner active={response.loading} className="create-sustainability-system">
      <div className="form-step">
        <div>
          <h1 className="page-title small">{t("sustainabilitySystem.createTitle")}</h1>
          <Paper className="page-content small">
            <h2>{t("sustainabilitySystem.step.baseData")}</h2>

            <form onSubmit={handleSubmit(onSubmit)} autoComplete={"off"}>
              <FormController
                control={control}
                name="systemSource"
                required
                label={t("sustainabilitySystem.systemSource")}
                render={({ field, fieldState, label }) => (
                  <TextField
                    error={fieldState.invalid}
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    label={label + " *"}
                    {...field}
                  />
                )}
              />
              <FormController
                control={control}
                name="name"
                required
                label={t("sustainabilitySystem.name")}
                render={({ field, fieldState, label }) => (
                  <TextField
                    error={fieldState.invalid}
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    label={label + " *"}
                    {...field}
                  />
                )}
              />
              <FormController
                control={control}
                name="systemVersion"
                required
                label={t("sustainabilitySystem.systemVersion")}
                render={({ field, fieldState, label }) => (
                  <TextField
                    error={fieldState.invalid}
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    label={label + " *"}
                    {...field}
                  />
                )}
              />
              <ProjectTypeSelect control={control} disabled={tenantType === TENANT_TYPE_PAST} singleSelect />
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label={t("sustainabilitySystem.description")}
                    {...field}
                    inputProps={{ maxLength: 255 }}
                  />
                )}
              />
              <div className="fabs">
                <Fab color="secondary" onClick={() => navigate(-1)}>
                  <CloseSVG className="fab-svg" />
                </Fab>
                <Fab type="submit" color="primary">
                  <SaveSVG className="fab-svg" />
                </Fab>
              </div>
            </form>
          </Paper>
        </div>
      </div>
    </LoadingOverlay>
  );
};
